/**
 * #1992 — une coupure réseau pendant un envoi de fichier laissait l'écran en
 * attente (délai du SDK Firebase : 70 s par appel), puis affichait un message
 * générique dans le bandeau du haut, hors de vue, sans moyen de réessayer.
 * Mesuré le 02/10/2026 : en Préparation, l'étiquette (PDF de 168 Ko, envoyé en
 * base64 dans `uploadBackofficePhoto`) n'atteignait jamais la fonction — trois
 * pré-vérifications OPTIONS, aucune exécution — sur une connexion qui perdait
 * des paquets par intermittence. Sur le terrain (Congo, livraison), le réseau
 * sera instable : ces écrans doivent supporter une coupure.
 *
 * Utilitaires PURS, partagés par tous les écrans qui envoient un fichier.
 */

/**
 * Coupure détectée par le CODE d'erreur du SDK Functions, jamais par le texte
 * (un message serveur explicite peut contenir n'importe quel mot) — règle de
 * #1945 / #1959, sortie de `vm-process-package` pour être la même partout :
 * - `functions/unavailable` : serveur injoignable ;
 * - `functions/deadline-exceeded` : délai dépassé, le serveur a pu terminer ;
 * - `functions/internal` SANS détail (message vide ou « internal ») : ce que
 *   rend le SDK quand la requête est coupée (réseau, corps > 10 Mo). Un
 *   `internal` AVEC un message serveur reste un refus serveur.
 * Une erreur sans code ni message est traitée comme une coupure.
 */
export function isInterruptedCall(err: any): boolean {
  const code = typeof err?.code === 'string' ? err.code : '';
  if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') return true;
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  const noDetail = !message || message.toLowerCase() === 'internal';
  return noDetail && (code === 'functions/internal' || code === '');
}

/**
 * Délai d'un envoi de fichier, proportionnel à sa taille : un délai fixe court
 * ferait échouer à tort un gros fichier sur un réseau de terrain lent, un délai
 * fixe long laisse l'agent attendre une requête déjà perdue.
 *
 * Décision du propriétaire (#1992) : « 30 s + corps à 32 kbit/s, jamais moins de 70 s, au
 * plus 300 s ». Mesuré sur une connexion réelle à ~64 kbit/s : 218 Ko en 28,6 s,
 * 546 Ko en 65 s — la première formule (256 kbit/s, 120 s au plus) coupait des
 * envois qui progressaient. 32 kbit/s = 4 000 octets/s ; 70 s est le délai par
 * défaut du SDK, qu'on ne raccourcit jamais.
 * Le corps envoyé fait à peu près la longueur de la chaîne base64.
 */
export const UPLOAD_TIMEOUT_MIN_S = 70;
export const UPLOAD_TIMEOUT_MAX_S = 300;
const UPLOAD_BYTES_PER_S = 32_000 / 8;

export function uploadTimeoutMs(payloadChars: number): number {
  const seconds = 30 + Math.max(0, payloadChars) / UPLOAD_BYTES_PER_S;
  return Math.round(Math.min(UPLOAD_TIMEOUT_MAX_S, Math.max(UPLOAD_TIMEOUT_MIN_S, seconds)) * 1000);
}

/** Traduction (`ProductionTranslationService.instant`) : tout texte affiché passe par une clé. */
export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/** Étape où l'action a échoué : l'envoi du fichier, ou l'enregistrement qui suit. */
export type ActionStep = 'upload' | 'save';

export interface ActionFailure {
  message: string;
  /** Réessayer sans risque (l'envoi n'a rien enregistré, ou le serveur a refusé). */
  retryable: boolean;
  /**
   * Coupure pendant l'ENREGISTREMENT : il a pu aboutir. L'écran propose
   * « Vérifier », qui relit le serveur, au lieu de « Réessayer » — une relance
   * à l'aveugle peut doubler un geste (pesée, livraison).
   */
  verifiable: boolean;
}

/**
 * Le message montré près du bouton, selon ce qui a échoué et à quelle étape.
 * - Coupure pendant l'ENVOI du fichier : rien n'est enregistré, on réessaie.
 * - Coupure pendant l'ENREGISTREMENT : il a pu aboutir côté serveur ; on fait
 *   vérifier avant de recommencer.
 * - Refus serveur explicite : son message, tel quel.
 * - Sinon : le texte générique de l'écran (`fallbackKey`).
 */
export function actionFailure(err: any, step: ActionStep, fallbackKey: string, t: TranslateFn): ActionFailure {
  if (isInterruptedCall(err)) {
    return step === 'upload'
      ? { message: t('BACKOFFICE.ACTION_ERROR.UPLOAD_INTERRUPTED'), retryable: true, verifiable: false }
      : { message: t('BACKOFFICE.ACTION_ERROR.SAVE_INTERRUPTED'), retryable: false, verifiable: true };
  }
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  const fromServer = typeof err?.code === 'string' && err.code.startsWith('functions/') && !!message
    && message.toLowerCase() !== 'internal';
  return { message: fromServer ? message : t(fallbackKey), retryable: true, verifiable: false };
}

/**
 * Issue de « Vérifier » : l'écran a relu le serveur après une coupure pendant
 * l'enregistrement.
 * - `recorded` : l'action a abouti, l'écran passe à la suite comme après un succès ;
 * - `absent` : rien n'a été écrit, « Réessayer » redevient sûr ;
 * - `unreachable` : la relecture a échoué à son tour, on propose de revérifier.
 */
export type VerifyOutcome = 'recorded' | 'absent' | 'unreachable';

/** Le message (et les boutons) qui suivent une vérification qui n'a pas trouvé l'action. */
export function verifyFailure(outcome: Exclude<VerifyOutcome, 'recorded'>, t: TranslateFn): ActionFailure {
  return outcome === 'absent'
    ? { message: t('BACKOFFICE.ACTION_ERROR.NOT_RECORDED'), retryable: true, verifiable: false }
    : { message: t('BACKOFFICE.ACTION_ERROR.VERIFY_UNREACHABLE'), retryable: false, verifiable: true };
}

/**
 * Relit le serveur (`read`) et juge (`recorded`) si l'action a abouti ; rend
 * aussi la valeur relue, pour que l'écran reparte de l'état serveur. Une
 * relecture qui échoue n'est jamais lue comme « rien n'a été enregistré ».
 */
export async function verifyRecorded<T>(
  read: () => Promise<T>,
  recorded: (value: T) => boolean,
): Promise<{ outcome: VerifyOutcome; value?: T }> {
  let value: T;
  try {
    value = await read();
  } catch {
    return { outcome: 'unreachable' };
  }
  return { outcome: recorded(value) ? 'recorded' : 'absent', value };
}

/**
 * Cache d'envoi : une relance ne renvoie pas un fichier déjà passé. La clé est
 * l'emplacement (dossier/nom) ; le fichier n'est réutilisé que s'il est
 * IDENTIQUE (même chaîne) — une nouvelle photo repart.
 */
export class UploadCache {
  private readonly done = new Map<string, { source: string; url: string }>();

  async upload(key: string, source: string, send: () => Promise<string>): Promise<string> {
    const previous = this.done.get(key);
    if (previous && previous.source === source) return previous.url;
    const url = await send();
    this.done.set(key, { source, url });
    return url;
  }

  clear(): void {
    this.done.clear();
  }
}
